import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogTitle, IconButton, List, ListItemButton, ListItemIcon,
  ListItemText, Tooltip, Typography,
} from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PaletteIcon from '@mui/icons-material/Palette';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { Loader, NeedsHousehold, PageHeader, useConfirm, useLoad, useNotify } from '../common';
import { PromptDialog } from './dialogs';
import { PLACE_COLORS, PLACE_ICONS, PlaceIcon, placeColors } from './placeLook';

const articles = (n: number) => (n === 1 ? '1 artigo' : `${n} artigos`);

type Prompt = { kind: 'new' } | { kind: 'rename'; location: T.StorageLocation } | { kind: 'zone'; location: T.StorageLocation };

/** Places at home and the zones inside them: rename, order, add zones, delete; how many items each holds. */
const Locations: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [confirm, confirmDialog] = useConfirm();
  const { data, error, loading, reload } = useLoad(() => api.getStorageLocations());
  const [sheet, setSheet] = useState<T.StorageLocation | null>(null);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  /** "Ícone e cor": the place, and the icon and colour being chosen. */
  const [look, setLook] = useState<{ place: T.StorageLocation; icon: string; color: string } | null>(null);
  const locations = data ?? [];
  const places = locations.filter(l => !l.parentPublicId);
  const zonesOf = (place: T.StorageLocation) => locations.filter(l => l.parentPublicId === place.publicId);
  /** Frozen batches in a freezer place and its zones. */
  const frozenIn = (place: T.StorageLocation) => zonesOf(place).reduce((sum, z) => sum + z.frozenCount, place.frozenCount);
  const siblingsOf = (l: T.StorageLocation) => (l.parentPublicId ? locations.filter(x => x.parentPublicId === l.parentPublicId) : places);

  const run = async (work: () => Promise<unknown>) => {
    try {
      await work();
      await reload();
    } catch (e: any) {
      notify(e.message, { severity: 'error' });
    }
  };

  const move = (location: T.StorageLocation, by: number) => {
    const order = siblingsOf(location).map(l => l.publicId);
    const index = order.indexOf(location.publicId);
    const other = index + by;
    if (index < 0 || other < 0 || other >= order.length) return;
    order[index] = order[other];
    order[other] = location.publicId;
    run(() => api.reorderStorageLocations(order));
  };

  const remove = async (location: T.StorageLocation) => {
    const what = location.parentPublicId ? 'esta zona' : 'este local e as suas zonas';
    if (!await confirm({
      title: `Apagar ${location.fullName}?`,
      text: `Apaga ${what}. O que lá estava continua contado em casa, sem local.`,
      confirmText: 'Apagar',
      danger: true,
    })) return;
    run(() => api.deleteStorageLocation(location.publicId));
  };

  const choose = (action: 'rename' | 'zone' | 'look' | 'freezer' | 'up' | 'down' | 'delete') => {
    const location = sheet;
    setSheet(null);
    if (!location) return;
    if (action === 'rename' || action === 'zone') setPrompt({ kind: action, location });
    else if (action === 'look') setLook({ place: location, icon: location.icon, color: location.color });
    else if (action === 'freezer') run(() => api.setStorageLocationFreezer(location.publicId, !location.isFreezer));
    else if (action === 'up') move(location, -1);
    else if (action === 'down') move(location, 1);
    else remove(location);
  };

  const submitPrompt = async (value: string): Promise<string | void> => {
    if (!prompt) return;
    try {
      if (prompt.kind === 'new') await api.createStorageLocation(value);
      else if (prompt.kind === 'zone') await api.createStorageLocation(value, prompt.location.publicId);
      else if (value !== prompt.location.name) await api.renameStorageLocation(prompt.location.publicId, value);
      await reload();
      return undefined;
    } catch (e: any) {
      return e.message;
    }
  };

  const row = (location: T.StorageLocation, zoneCount: number) => {
    const isZone = !!location.parentPublicId;
    return (
      <ListItemButton key={location.publicId} onClick={() => setSheet(location)} sx={{ pl: isZone ? 4 : 1, borderRadius: 1, minHeight: 48 }}>
        {!isZone && (
          <Box sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: placeColors(location.color).soft, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0 }}>
            <PlaceIcon icon={location.icon} color={location.color} fontSize="small" />
          </Box>
        )}
        <ListItemText
          primary={isZone ? `› ${location.name}` : location.name}
          secondary={!isZone && zoneCount > 0 ? (zoneCount === 1 ? '1 zona' : `${zoneCount} zonas`) : undefined}
          slotProps={{ primary: { sx: { fontWeight: isZone ? 400 : 600 } } }}
        />
        {!isZone && location.isFreezer && (
          <Chip
            size="small"
            icon={<AcUnitIcon />}
            label={frozenIn(location) > 0 ? `congelador · ${frozenIn(location)}` : 'congelador'}
            color="info"
            sx={{ ml: 1 }}
          />
        )}
        <Chip
          size="small"
          label={articles(location.itemCount)}
          color={location.itemCount === 0 ? 'default' : 'info'}
          variant="outlined"
          sx={{ ml: 1 }}
        />
      </ListItemButton>
    );
  };

  const sheetSiblings = sheet ? siblingsOf(sheet) : [];
  const sheetIndex = sheet ? sheetSiblings.findIndex(l => l.publicId === sheet.publicId) : -1;

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/casa/em-casa')} sx={{ mb: 1 }}>Em casa</Button>
      <PageHeader
        title="Locais e zonas"
        subtitle="Os locais da casa (Despensa, Frigorífico…) e as zonas dentro deles (Armário de cima). Toca num para mudar o nome, a ordem, juntar zonas, marcar como congelador ou apagar."
        actions={(
          <>
            <Tooltip title="Atualizar"><IconButton onClick={reload}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPrompt({ kind: 'new' })}>Novo local</Button>
          </>
        )}
      />
      <Loader loading={loading} error={error} onRetry={reload} hasData={data !== undefined}>
        {locations.length === 0 ? (
          <Card variant="outlined" sx={{ my: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Ainda não há locais</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Toca em «Novo local» ou diz onde as coisas estão («o arroz está no armário de cima da despensa») e os locais e zonas são criados sozinhos.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <List disablePadding>
            {places.map(place => {
              const zones = zonesOf(place);
              return (
                <React.Fragment key={place.publicId}>
                  {row(place, zones.length)}
                  {zones.map(zone => row(zone, 0))}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Loader>

      <Dialog open={sheet !== null} onClose={() => setSheet(null)} maxWidth="xs" fullWidth>
        {sheet && (
          <>
            <DialogTitle sx={{ pb: 0 }}>{sheet.fullName}</DialogTitle>
            <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
              {sheet.itemCount === 1 ? '1 artigo guardado aqui' : `${sheet.itemCount} artigos guardados aqui`}
            </Typography>
            <List>
              <ListItemButton onClick={() => choose('rename')}>
                <ListItemIcon><EditIcon /></ListItemIcon>
                <ListItemText primary="Mudar o nome" />
              </ListItemButton>
              {!sheet.parentPublicId && (
                <ListItemButton onClick={() => choose('zone')}>
                  <ListItemIcon><AddIcon /></ListItemIcon>
                  <ListItemText primary="Juntar uma zona" secondary="Uma parte deste local: prateleira, gaveta…" />
                </ListItemButton>
              )}
              {!sheet.parentPublicId && (
                <ListItemButton onClick={() => choose('look')}>
                  <ListItemIcon><PaletteIcon /></ListItemIcon>
                  <ListItemText primary="Ícone e cor" secondary="Como aparece no mapa de Em casa" />
                </ListItemButton>
              )}
              {!sheet.parentPublicId && (
                <ListItemButton onClick={() => choose('freezer')}>
                  <ListItemIcon><AcUnitIcon color={sheet.isFreezer ? 'info' : undefined} /></ListItemIcon>
                  <ListItemText
                    primary={sheet.isFreezer ? 'Deixar de ser congelador' : 'É um congelador'}
                    secondary={sheet.isFreezer
                      ? 'Só depois de mudar ou acabar o que lá está congelado'
                      : 'O que vai para o congelador pode ficar aqui (e nas suas zonas)'}
                  />
                </ListItemButton>
              )}
              {sheetIndex > 0 && (
                <ListItemButton onClick={() => choose('up')}>
                  <ListItemIcon><ArrowUpwardIcon /></ListItemIcon>
                  <ListItemText primary="Subir" />
                </ListItemButton>
              )}
              {sheetIndex >= 0 && sheetIndex < sheetSiblings.length - 1 && (
                <ListItemButton onClick={() => choose('down')}>
                  <ListItemIcon><ArrowDownwardIcon /></ListItemIcon>
                  <ListItemText primary="Descer" />
                </ListItemButton>
              )}
              <ListItemButton onClick={() => choose('delete')}>
                <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
                <ListItemText
                  primary="Apagar"
                  secondary={sheet.itemCount > 0 ? 'Os artigos ficam sem este local' : undefined}
                  slotProps={{ primary: { sx: { color: 'error.main' } } }}
                />
              </ListItemButton>
            </List>
          </>
        )}
      </Dialog>

      <Dialog open={look !== null} onClose={() => setLook(null)} maxWidth="xs" fullWidth>
        {look && (
          <>
            <DialogTitle>{`Ícone e cor de ${look.place.name}`}</DialogTitle>
            <Box sx={{ px: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Ícone</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 2 }}>
                {PLACE_ICONS.map(i => (
                  <Tooltip key={i.key} title={i.name}>
                    <IconButton
                      aria-label={i.name}
                      aria-pressed={look.icon === i.key}
                      onClick={() => setLook({ ...look, icon: i.key })}
                      sx={{ borderRadius: 2, bgcolor: look.icon === i.key ? placeColors(look.color).soft : undefined, border: 2, borderColor: look.icon === i.key ? placeColors(look.color).strong : 'transparent' }}
                    >
                      <PlaceIcon icon={i.key} color={look.color} />
                    </IconButton>
                  </Tooltip>
                ))}
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Cor</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {PLACE_COLORS.map(c => (
                  <Tooltip key={c.key} title={c.name}>
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label={c.name}
                      aria-pressed={look.color === c.key}
                      onClick={() => setLook({ ...look, color: c.key })}
                      onKeyDown={e => { if (e.key === 'Enter') setLook({ ...look, color: c.key }); }}
                      sx={{ width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', bgcolor: placeColors(c.key).soft, border: 3, borderColor: look.color === c.key ? placeColors(c.key).strong : placeColors(c.key).soft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: placeColors(c.key).strong }} />
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, px: 3, pb: 2, justifyContent: 'flex-end' }}>
              {look.place.hasOwnLook && (
                <Button sx={{ mr: 'auto' }} onClick={() => { const place = look.place; setLook(null); run(() => api.setStorageLocationLook(place.publicId, null, null)); }}>
                  Como o nome sugere
                </Button>
              )}
              <Button onClick={() => setLook(null)}>Cancelar</Button>
              <Button variant="contained" onClick={() => { const chosen = look; setLook(null); run(() => api.setStorageLocationLook(chosen.place.publicId, chosen.icon, chosen.color)); }}>
                Guardar
              </Button>
            </Box>
          </>
        )}
      </Dialog>

      <PromptDialog
        open={prompt !== null}
        title={prompt?.kind === 'new' ? 'Novo local' : prompt?.kind === 'zone' ? `Zona em ${prompt.location.name}` : 'Mudar o nome'}
        text={prompt?.kind === 'rename' ? prompt.location.fullName : undefined}
        placeholder={prompt?.kind === 'new' ? 'Ex.: Despensa, Frigorífico, Arrecadação' : prompt?.kind === 'zone' ? 'Ex.: Armário de cima, Gaveta dos legumes' : undefined}
        initial={prompt?.kind === 'rename' ? prompt.location.name : ''}
        confirmText={prompt?.kind === 'rename' ? 'Guardar' : 'Juntar'}
        onClose={() => setPrompt(null)}
        onSubmit={submitPrompt}
      />
      {confirmDialog}
    </Box>
  );
};

const StorageLocationsPage: React.FC = () => <NeedsHousehold><Locations /></NeedsHousehold>;

export default StorageLocationsPage;
